import React, { useState, useEffect } from 'react';
import { User, Mail, Key, Save, X, Edit3 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { http } from '../../lib/api';

export default function MiPerfil({ isOpen, onClose }) {
  const { user, logout } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    nombre_completo: '',
    email: '',
    password_actual: '',
    password_nueva: '',
    confirmar_password: ''
  });
  const [loading, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [mensaje, setMensaje] = useState('');

  // Cargar datos del perfil cuando se abre el modal
  useEffect(() => {
    if (isOpen && user) {
      cargarPerfil();
    }
  }, [isOpen, user]);

  const cargarPerfil = async () => {
    try {
      setLoadingProfile(true);
      const token = localStorage.getItem('token');
      const response = await http.get('/perfil/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData({
        nombre_completo: response.data.nombre_completo || '',
        email: response.data.email || '',
        password_actual: '',
        password_nueva: '',
        confirmar_password: ''
      });
    } catch (error) {
      console.error('Error cargando perfil:', error);
      setMensaje('Error al cargar los datos del perfil');
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMensaje('');

    try {
      const token = localStorage.getItem('token');
      
      // Validaciones
      if (formData.password_nueva && formData.password_nueva !== formData.confirmar_password) {
        setMensaje('Las contraseñas no coinciden');
        return;
      }

      if (formData.password_nueva && formData.password_nueva.length < 6) {
        setMensaje('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      // Actualizar datos básicos del perfil
      const datosBasicos = {
        nombre_completo: formData.nombre_completo,
        email: formData.email
      };

      await http.put('/perfil/me', datosBasicos, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Si hay cambio de contraseña, actualizar por separado
      if (formData.password_nueva && formData.password_actual) {
        const datosPassword = {
          password_actual: formData.password_actual,
          password_nueva: formData.password_nueva
        };

        await http.put('/perfil/password', datosPassword, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setMensaje('Perfil actualizado correctamente');
      setEditMode(false);
      
      // Limpiar campos de contraseña
      setFormData(prev => ({
        ...prev,
        password_actual: '',
        password_nueva: '',
        confirmar_password: ''
      }));

      // Recargar datos del usuario en el contexto
      setTimeout(() => {
        window.location.reload(); // Forzar recarga para actualizar el header
      }, 1500);

    } catch (error) {
      console.error('Error:', error);
      const mensajeError = error.response?.data?.detail || 'Error al actualizar el perfil';
      setMensaje(mensajeError);
    } finally {
      setSaving(false);
    }
  };

  const cancelarEdicion = () => {
    // Recargar datos originales
    cargarPerfil();
    setEditMode(false);
    setMensaje('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <User size={20} />
            Mi Perfil
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {loadingProfile ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-gray-500">Cargando datos del perfil...</p>
            </div>
          ) : (
            <>
              {/* Avatar y info básica */}
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto mb-3">
                  {formData.nombre_completo?.split(' ').map(n => n[0]).join('').toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <h3 className="font-semibold text-gray-900">{formData.nombre_completo || user?.username}</h3>
                <p className="text-sm text-gray-500">{user?.rol}</p>
              </div>

              {/* Formulario */}
              <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nombre completo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre completo
              </label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="nombre_completo"
                  value={formData.nombre_completo}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    editMode ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                    editMode ? 'bg-white' : 'bg-gray-50'
                  }`}
                />
              </div>
            </div>

            {/* Cambio de contraseña - solo visible en modo edición */}
            {editMode && (
              <>
                <div className="border-t pt-4 mt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Cambiar contraseña</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Contraseña actual
                      </label>
                      <div className="relative">
                        <Key size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="password"
                          name="password_actual"
                          value={formData.password_actual}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Ingresa tu contraseña actual"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Nueva contraseña
                      </label>
                      <div className="relative">
                        <Key size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="password"
                          name="password_nueva"
                          value={formData.password_nueva}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Nueva contraseña"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        Confirmar nueva contraseña
                      </label>
                      <div className="relative">
                        <Key size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="password"
                          name="confirmar_password"
                          value={formData.confirmar_password}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          placeholder="Confirmar nueva contraseña"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Mensaje */}
            {mensaje && (
              <div className={`p-3 rounded-lg text-sm ${
                mensaje.includes('Error') ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
              }`}>
                {mensaje}
              </div>
              )}
            </form>
            </>
          )}
        </div>

        {/* Footer con botones */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          {!editMode ? (
            <button
              onClick={() => setEditMode(true)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Edit3 size={16} />
              Editar perfil
            </button>
          ) : (
            <>
              <button
                onClick={cancelarEdicion}
                type="button"
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                Guardar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}